import type { Negocio } from "./tipos";

/** Campo de orden especial de Leads: «a quién llamar primero» (lo resuelve el proveedor de datos). */
export const ORDEN_LLAMADA = "orden_llamada";

export type FichaParaLlamar = Pick<
  Negocio,
  | "origen"
  | "ultimo_contacto"
  | "created_at"
  | "fecha_siguiente_paso"
  | "prospect_prioridad"
> &
  Partial<Pick<Negocio, "hora_siguiente_paso">>;

/** Cuándo toca volver a llamar (fecha y hora del siguiente paso, hora local), o null. */
export const momentoReintento = (f: FichaParaLlamar): Date | null => {
  if (!f.fecha_siguiente_paso) return null;
  const [a, m, d] = String(f.fecha_siguiente_paso)
    .slice(0, 10)
    .split("-")
    .map(Number);
  const [h, min] = (f.hora_siguiente_paso ?? "00:00").split(":").map(Number);
  return new Date(a, m - 1, d, h || 0, min || 0);
};

/**
 * Grupo de una ficha en la cola de llamadas (auditoría 30/10/2026):
 * 0 · llegó por la web y aún no se ha llamado (cuanto antes);
 * 1 · tiene el reintento vencido (ya es la hora);
 * 2 · nunca llamada (por prioridad de Prospect: sin prioridad —propias, CSV— primero, luego 1, 2, 3);
 * 3 · llamada antes, sin hora de reintento;
 * 4 · reintento todavía por llegar.
 */
export const grupoLlamada = (f: FichaParaLlamar, ahora: Date): number => {
  const reintento = momentoReintento(f);
  if (!f.ultimo_contacto && !reintento) return f.origen === "web" ? 0 : 2;
  if (reintento) return reintento <= ahora ? 1 : 4;
  return 3;
};

const tiempo = (v: string | null | undefined) =>
  v ? new Date(v).getTime() : Number.POSITIVE_INFINITY;

/** La cola de Leads en el orden en que conviene llamar. */
export const ordenParaLlamar = <T extends FichaParaLlamar>(
  fichas: T[],
  ahora: Date = new Date(),
): T[] =>
  [...fichas].sort((a, b) => {
    const ga = grupoLlamada(a, ahora);
    const gb = grupoLlamada(b, ahora);
    if (ga !== gb) return ga - gb;
    if (ga === 1 || ga === 4)
      return (
        (momentoReintento(a)?.getTime() ?? 0) -
        (momentoReintento(b)?.getTime() ?? 0)
      );
    if (ga === 2) {
      const pa = a.prospect_prioridad ?? 0;
      const pb = b.prospect_prioridad ?? 0;
      if (pa !== pb) return pa - pb;
    }
    if (ga === 3) return tiempo(a.ultimo_contacto) - tiempo(b.ultimo_contacto);
    return tiempo(a.created_at) - tiempo(b.created_at);
  });

const hora = (d: Date) =>
  d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });

/** «Hoy 16:30», «Mañana 10:00» o «lun 02/11, 10:00». */
export const textoMomento = (
  d: Date,
  ahora: Date,
  t: (k: string) => string,
) => {
  const dia = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const hoy = new Date(
    ahora.getFullYear(),
    ahora.getMonth(),
    ahora.getDate(),
  ).getTime();
  const dias = Math.round((dia - hoy) / 86_400_000);
  if (dias === 0) return `${t("nevora.llamadas.hoy")} ${hora(d)}`;
  if (dias === 1) return `${t("nevora.llamadas.manana")} ${hora(d)}`;
  return `${d.toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "2-digit" })}, ${hora(d)}`;
};
